// src/components/Drivers.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import { API_URL } from "../../api-config";

const formatTime = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const LABELS = {
  "CAPITALCOM:COPPER": "COPPER",
  "OANDA:AUDJPY": "AUDJPY",
  "TVC:DXY": "DXY",
  "OANDA:USDMXN": "USDMXN",
};

const Drivers = () => {
  const [drivers, setDrivers] = useState({});
  const [meta, setMeta] = useState({ window: 10, timeframe: "5m" });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [sel, setSel] = useState("CAPITALCOM:COPPER");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setErr("");
        const res = await axios.get(`${API_URL}/trading-api/historical/drivers`);
        if (!mounted) return;

        setMeta(res.data?.meta ?? { window: 10, timeframe: "5m" });
        setDrivers(res.data?.drivers ?? {});
      } catch (e) {
        setErr(e?.message || "Error al cargar drivers");
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const chartData = useMemo(() => {
    const item = drivers?.[sel];
    if (!item) return [];
    const corr = Array.isArray(item.correlation_close) ? item.correlation_close : [];
    return corr.map((p) => ({
      ts: p.ts,
      value: typeof p.value === "number" ? +p.value.toFixed(4) : null,
    }));
  }, [drivers, sel]);

  const lastCorr = useMemo(() => {
    if (!chartData.length) return null;
    return chartData[chartData.length - 1]?.value ?? null;
  }, [chartData]);

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="bg-[#1f2a40] rounded-2xl shadow p-4 md:p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-3">
            <h2 className="text-white font-semibold">
              Correlación Rolling ({meta.window}) — {meta.timeframe}
            </h2>
            <select
              className="bg-[#0f172a] text-slate-200 text-sm rounded px-2 py-1 border border-[#2a344b]"
              value={sel}
              onChange={(e) => setSel(e.target.value)}
            >
              {Object.keys(LABELS).map((k) => (
                <option key={k} value={k}>
                  {LABELS[k]}
                </option>
              ))}
            </select>
          </div>

          <div className="text-sm text-slate-300">
            {typeof lastCorr === "number" && (
              <>
                Última ({LABELS[sel]}):{" "}
                <span
                  className={`font-mono ${
                    lastCorr >= 0 ? "text-emerald-400" : "text-pink-300"
                  }`}
                >
                  {lastCorr.toFixed(4)}
                </span>
              </>
            )}
          </div>
        </div>

        {loading ? (
          <div className="text-slate-300">Cargando…</div>
        ) : err ? (
          <div className="text-red-300">Error: {err}</div>
        ) : chartData.length === 0 ? (
          <div className="text-slate-300">No hay datos de correlación.</div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid stroke="#2a344b" />
                <XAxis
                  dataKey="ts"
                  tick={{ fill: "#cbd5e1", fontSize: 11 }}
                  tickFormatter={formatTime}
                  minTickGap={16}
                />
                <YAxis domain={[-1, 1]} tick={{ fill: "#cbd5e1" }} tickCount={5} />
                <Tooltip
                  contentStyle={{ backgroundColor: "#1f2a40", borderColor: "#2a344b" }}
                  labelFormatter={(v) => new Date(v).toLocaleString()}
                  formatter={(v) => [v, "corr"]}
                />
                <ReferenceLine y={0} stroke="#64748b" strokeDasharray="3 3" />
                <Line
                  type="monotone"
                  dataKey="value"
                  dot={false}
                  stroke="#60a5fa"
                  strokeWidth={2}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Tabla de últimos valores por driver */}
      <div className="bg-[#1f2a40] rounded-2xl shadow p-4 md:p-5">
        <h3 className="text-white font-semibold mb-3">Resumen Drivers</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-slate-300 border-b border-[#2a344b]">
                <th className="text-left py-2 pr-4">Símbolo</th>
                <th className="text-left py-2 pr-4">Último Precio</th>
                <th className="text-left py-2 pr-4">Última Correlación</th>
                <th className="text-left py-2 pr-4">Puntos Alineados</th>
                <th className="text-left py-2 pr-4">Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(drivers).map(([sym, info]) => {
                const corr = Array.isArray(info.correlation_close) ? info.correlation_close : [];
                const last = corr.length ? corr[corr.length - 1] : null;
                const lastCorr = (last && typeof last.value === "number") ? last.value : null;
                return (
                  <tr key={sym} className="text-slate-200 border-b border-[#2a344b]">
                    <td className="py-2 pr-4">{LABELS[sym] || sym}</td>
                    <td className="py-2 pr-4 font-mono">
                      {typeof info.last_price === "number" ? info.last_price.toFixed(4) : "—"}
                    </td>
                    <td className={`py-2 pr-4 font-mono ${lastCorr >= 0 ? "text-emerald-400" : "text-pink-300"}`}>
                      {typeof lastCorr === "number" ? lastCorr.toFixed(4) : "—"}
                    </td>
                    <td className="py-2 pr-4">{info.merged_rows ?? 0}</td>
                    <td className="py-2 pr-4">
                      {info.last_ts ? new Date(info.last_ts).toLocaleString() : "—"}
                    </td>
                  </tr>
                );
              })}
              {!Object.keys(drivers).length && (
                <tr><td className="py-2 pr-4" colSpan={5}>Sin datos.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Drivers;
